/**
 * What is on sale.
 *
 * Two terms of the same thing, not two products. Everything the pass opens is
 * open on both — the only difference is how long, because the two candidates
 * this app has are separated by their calendar and nothing else. Somebody with
 * a telc date already booked is preparing for weeks; somebody sitting the state
 * exam for citizenship is waiting on a session held a few times a year, and
 * ninety days does not reach it. A second, *cheaper* tier was considered and
 * refused: it would have moved the comparison from the 800-1200 zł the exam
 * itself costs onto our own two prices, which is the one comparison we win by
 * losing.
 *
 * The longer term is cheaper per day and says so on the page rather than
 * claiming a discount — `formatPerDay` is what it prints, so the saving cannot
 * be restated wrongly in copy. It is not a subscription: nothing renews, and a
 * second purchase stacks days onto the ones still running.
 *
 * Prices live here, in one place the browser and the Worker both read: the
 * page must never state a figure the checkout does not charge. What was
 * actually paid is copied into `orders` at purchase time, so changing this
 * table never rewrites anybody's history.
 */

export interface Plan {
  id: string;
  /** How long access lasts, counted from the moment the payment lands. */
  days: number;
  /** In grosze — Stripe counts in the currency's smallest unit. */
  amountMinor: number;
  /** BLIK requires the account currency, which is PLN. */
  currency: "pln";
}

/**
 * The default term, and the price every public page quotes. A reader deciding
 * whether to practise at all is not choosing a term yet, so the marketing pages
 * carry this one figure and the choice waits until checkout.
 */
export const PASS: Plan = {
  id: "pass90",
  days: 90,
  amountMinor: 12900,
  currency: "pln",
};

/** For the session that is still months away — the state exam's, usually. */
export const PASS_LONG: Plan = {
  id: "pass180",
  days: 180,
  amountMinor: 19900,
  currency: "pln",
};

/** Shortest first: the page reads as terms of one offer, not a ladder. */
export const PLANS: Plan[] = [PASS, PASS_LONG];

export const planById = (id: string): Plan | undefined => PLANS.find((p) => p.id === id);

/**
 * Sessions of the marked oral exam, bought on top of what a pass brings.
 *
 * An add-on, not a way in: a pack spends its sessions on sets the account can
 * already open — the free ones, or all of them while a pass runs — so a set is
 * still either open or not, and nothing on the set page has to explain a half.
 * They never expire: a session costs us about half a złoty, and a date on
 * something bought is one more thing to explain and nothing to protect.
 *
 * Priced near the per-session figure of the oral-practice services already on
 * the market; the pass, with its ten sessions a quarter and the whole written
 * part, stays the better buy, which is the point.
 */
export interface OralPack {
  id: string;
  sessions: number;
  amountMinor: number;
  currency: "pln";
}

export const ORAL_PACKS: OralPack[] = [
  { id: "oral5", sessions: 5, amountMinor: 2500, currency: "pln" },
  { id: "oral10", sessions: 10, amountMinor: 3900, currency: "pln" },
  { id: "oral20", sessions: 20, amountMinor: 6900, currency: "pln" },
];

export const packById = (id: string): OralPack | undefined => ORAL_PACKS.find((p) => p.id === id);

/** Anything a checkout can sell. */
export type Product = Plan | OralPack;

export const isPack = (p: Product): p is OralPack => "sessions" in p;

export const productById = (id: string): Product | undefined => planById(id) ?? packById(id);

/**
 * The Stripe price's `lookup_key` for a product — how the checkout finds the
 * price `scripts/stripe-products.ts` created, so Stripe's own reports group
 * sales by product instead of by an inline name.
 */
export const lookupKeyOf = (p: Product): string => `polskiegzamin_${p.id}`;

/** "129 zł" — the price as it is written on the page, not as it is stored. */
export const formatPrice = (plan: { amountMinor: number }): string =>
  `${(plan.amountMinor / 100).toLocaleString("pl-PL", {
    minimumFractionDigits: plan.amountMinor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} zł`;

/**
 * "1,11 zł" — what a day of the term costs, which is the only honest way to put
 * two terms side by side. Computed, never written down: a percentage in copy
 * goes stale the moment either price moves, and nothing would catch it.
 */
export const formatPerDay = (plan: Plan): string =>
  `${(plan.amountMinor / plan.days / 100).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} zł`;

export const DAY_MS = 86_400_000;

/** "3,90 zł" — what one session of a pack costs. Computed, for the same reason as `formatPerDay`. */
export const formatPerSession = (pack: OralPack): string =>
  `${(pack.amountMinor / pack.sessions / 100).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} zł`;
